import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  authService,
  InterfacePayload,
  interfaceRepo,
  IntExtInterfacePayload,
  intExtPayload,
  partnerService,
  userService,
} from '../lib/api-services';
import { AuthUser, handleApiError } from '../lib/api-client';
import { CreateFormValues } from '@/components/VersionRule/CreateFormDailog';

type CreatePortalFormPayload = {
  data: CreateFormValues;
  formType: string;
};

type Togglepayload = {
  id: number;
  formType: string;
};

// ==================== AUTH HOOKS ====================

export function useAuthMe(options?: UseQueryOptions<AuthUser, Error>) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = 'https://bprm-ai-portal.workfinity.com/login';
    },
    onError: (error) => {
      console.error('Logout error:', handleApiError(error));
    },
  });
}

//===================Partners API===============
// export function usePartnerData(pageNumber: number, pageSize?: number, partnerName?: string, partnerType?: string, partnerSubType?: string) {
//   return useQuery({
//     queryKey: ['partners', pageNumber, pageSize, partnerName, partnerType, partnerSubType],
//     queryFn: () => partnerService.getPartnersData({
//       pageNumber,
//       pageSize,
//       partnerName,
//       partnerType,
//       partnerSubType,
//     }),
//   });
// }

// Audit log Get Hook
export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ["auditLog", filters],
    queryFn: () => userService.getAuditLog(filters),
  });
}

// ==================== INTERFACE REPOSITORY HOOKS ====

export function useInterfaceRepository(page: number = 1, pageSize: number = 10, intId: string, intName?: string, intType?: string) {
  return useQuery({
    queryKey: ['interfaceRepository', page, pageSize, intId, intName, intType],
    queryFn: () => interfaceRepo.getInterfaceRepository({
      page,
      pageSize,
      intId,
      intName,
      intType,
    }),
  });
}

export function createInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InterfacePayload) => interfaceRepo.createInterface(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interfaceRepository'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function editInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: InterfacePayload }) =>
      interfaceRepo.editInterface(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interfaceRepository'] });
    },
    onError: (error) => {
      console.error('Edit interface error:', handleApiError(error));
    },
  });
}

export function deleteInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => interfaceRepo.deleteInterface(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interfaceRepository'] });
    },
    onError: (error) => {
      console.error('Delete interface error:', handleApiError(error));
    },
  });
}

export function toggleInterfaceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => interfaceRepo.statusToggleInterface(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interfaceRepository'] });
    },
    onError: (error) => {
      console.error('Toggle interface status error:', handleApiError(error));
    },
  });
}


// ==================== Internal/External APIs ====================

export function useIntExtInterface(page: number = 1, pageSize: number = 10, InterfaceId?: string, InterfaceName?: string, InterfaceType?: string) {
  return useQuery({
    queryKey: ['intExtInterface', page, pageSize, InterfaceId, InterfaceName, InterfaceType],
    queryFn: () => interfaceRepo.getIntExtInterface({
      page,
      pageSize,
      InterfaceId,
      InterfaceName,
      InterfaceType,
    }),
  });
}

export function useNonAddedIntExtInterface(page: number = 1, pageSize: number = 10, InterfaceId?: string, InterfaceName?: string, InterfaceType?: string) {
  return useQuery({
    queryKey: ['intNonAddedExtInterface', page, pageSize, InterfaceId, InterfaceName, InterfaceType],
    queryFn: () => interfaceRepo.getNonAddedIntExtInterface({
      page,
      pageSize,
      InterfaceId,
      InterfaceName,
      InterfaceType,
    }),
  });
}

export function createIntExtInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: intExtPayload) => interfaceRepo.createIntExtInterface(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intExtInterface'] });
      queryClient.invalidateQueries({ queryKey: ['interfaceRepository'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function editIntExtInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: IntExtInterfacePayload }) =>
      interfaceRepo.editIntExtInterface(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intExtInterface'] });
    },
    onError: (error) => {
      console.error('Edit interface error:', handleApiError(error));
    },
  });
}

export function deleteIntExtInterfaceData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => interfaceRepo.deleteIntExtInterface(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intExtInterface'] });
    },
    onError: (error) => {
      console.error('Delete interface error:', handleApiError(error));
    },
  });
}